import { FC } from "react";

interface QueryProgressTrackerProps {
  currentState: string;
  isHost: boolean;
}

const QueryProgressTracker: FC<QueryProgressTrackerProps> = ({
  currentState,
  isHost,
}) => {
  const steps = [
    {
      path: 1,
      state: "send_message",
      label: "Initial Inquiry",
      description: isHost ? "Inquiry received" : "Inquiry sent",
    },
    {
      path: 2,
      state: "accepted",
      label: "Booking Request",
      description: isHost ? "Request received" : "Request sent",
    },
    {
      path: 3,
      state: "invoice_sent",
      label: "Payment",
      description: "Awaiting payment",
    },
    {
      path: 4,
      state: "confirmed",
      label: "Confirmed",
      description: "Booking complete",
    },
  ];

  // Find active step - default to step 1 if state matches send_message or booking_invitation
  let activeStepIndex = steps.findIndex((s) => s.state === currentState);
  
  // If state is booking_invitation, keep it at step 1 (initial inquiry)
  if (currentState === "booking_invitation") {
    activeStepIndex = 0;
  }
  
  // Default to 0 if not found
  if (activeStepIndex === -1) {
    activeStepIndex = 0;
  }

  return (
    <div className="relative border border-blue-200 dark:border-blue-700 bg-white dark:bg-neutral-900 p-6 md:p-8 rounded-2xl shadow-sm">
      <div className="flex items-center justify-between relative">
        {steps.map((step, index) => {
          const isActive = index <= activeStepIndex;
          const isCurrentStep = index === activeStepIndex;
          const isCompleted = index < activeStepIndex;

          

          return (
            <div
              key={step.path}
              className="flex flex-col items-center flex-1 relative"
            >
              {/* Step Circle */}
              <div className="relative z-10 flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 text-base font-bold transition-all duration-300 shadow-md ${
                    isCompleted
                      ? "bg-green-500 text-white"
                      : isCurrentStep
                      ? "bg-blue-600 text-white ring-4 ring-blue-200 dark:ring-blue-400/30 scale-110"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {isCompleted ? (
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    step.path
                  )}
                </div>

                {/* Step Label */}
                <div className="text-center">
                  <p
                    className={`text-sm font-semibold mb-1 ${
                      isActive
                        ? "text-gray-900 dark:text-gray-100"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {step.label}
                  </p>
                  <p
                    className={`text-xs ${
                      isActive
                        ? "text-gray-600 dark:text-gray-300"
                        : "text-gray-400 dark:text-gray-500"
                    }`}
                  >
                    {step.description}
                  </p>
                </div>
              </div>

              {/* Connecting Line */}
              {index < steps.length - 1 && (
                <div className="absolute top-6 left-[50%] w-full h-[2px] -z-0 hidden md:block">
                  <div
                    className={`h-full transition-all duration-300 ${
                      isCompleted
                        ? "bg-green-500"
                        : "bg-gray-300 dark:bg-gray-600"
                    }`}
                  ></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Optional: Show invitation badge if host sent invitation */}
      {currentState === "booking_invitation" && (
        <div className="mt-6 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
          <p className="text-sm text-center text-blue-800 dark:text-blue-200 font-medium">
            {isHost
              ? "✨ Invitation sent - waiting for booking request"
              : "✨ Host has invited you to book this property"}
          </p>
        </div>
      )}
    </div>
  );
};

export default QueryProgressTracker;